import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Check, RotateCw, Maximize2 } from 'lucide-react';

/**
 * PuzzleCropModal
 *
 * Lightweight, dependency-free, touch+mouse crop UI for sudoku-puzzle
 * photos. Shows the user's selected image with a square crop box they
 * can drag and resize. When they confirm, it produces a square JPEG of
 * just the puzzle (downsampled to 1600px max edge, quality 0.88).
 *
 * Why square: OCR works best when the model isn't distracted by margins,
 * captions, page numbers, or adjacent puzzles. A tight crop on the grid
 * dramatically improves accuracy.
 *
 * Props
 *   imageFile        : File from <input type=file>
 *   onCancel()       : user dismissed the modal
 *   onConfirm(blob)  : user confirmed; receives a JPEG Blob ready to upload
 */
export default function PuzzleCropModal({ imageFile, onCancel, onConfirm }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [imgDims, setImgDims] = useState({ w: 0, h: 0 });
  // Crop box in CSS pixels relative to the displayed image element.
  // We always keep the crop square.
  const [crop, setCrop] = useState({ x: 0, y: 0, size: 0 });
  const [busy, setBusy] = useState(false);
  const [rotation, setRotation] = useState(0); // 0 / 90 / 180 / 270

  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const dragRef = useRef({ mode: null, startX: 0, startY: 0, startCrop: null });

  // Read the file into a data URL.
  useEffect(() => {
    if (!imageFile) return;
    const reader = new FileReader();
    reader.onload = (e) => setImgUrl(e.target.result);
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  // Initialize crop box to a centered ~80% square once the image loads.
  const onImgLoad = () => {
    const el = imgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    setImgDims({ w, h });
    const size = Math.floor(Math.min(w, h) * 0.8);
    setCrop({
      x: Math.round((w - size) / 2),
      y: Math.round((h - size) / 2),
      size,
    });
  };

  // Pointer event helpers — works for both mouse and touch via pointer events
  const getPos = useCallback((e) => {
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  }, []);

  const onPointerDown = useCallback((mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { x, y } = getPos(e);
    dragRef.current = {
      mode,
      startX: x,
      startY: y,
      startCrop: { ...crop },
    };
  }, [crop, getPos]);

  const onPointerMove = useCallback((e) => {
    if (!dragRef.current.mode) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const dx = x - dragRef.current.startX;
    const dy = y - dragRef.current.startY;
    const start = dragRef.current.startCrop;
    const { w, h } = imgDims;
    if (!w || !h) return;

    if (dragRef.current.mode === 'move') {
      const newX = Math.max(0, Math.min(w - start.size, start.x + dx));
      const newY = Math.max(0, Math.min(h - start.size, start.y + dy));
      setCrop({ x: newX, y: newY, size: start.size });
    } else if (dragRef.current.mode === 'resize') {
      // Resize from bottom-right corner; keep square (use max of dx/dy)
      const delta = Math.max(dx, dy);
      const maxSize = Math.min(w - start.x, h - start.y);
      const minSize = 80;
      const newSize = Math.max(minSize, Math.min(maxSize, start.size + delta));
      setCrop({ x: start.x, y: start.y, size: newSize });
    }
  }, [getPos, imgDims]);

  const onPointerUp = useCallback(() => {
    dragRef.current = { mode: null, startX: 0, startY: 0, startCrop: null };
  }, []);

  // Listen on window so dragging works even if pointer leaves the box
  useEffect(() => {
    const handlers = [
      ['mousemove', onPointerMove],
      ['mouseup', onPointerUp],
      ['touchmove', onPointerMove],
      ['touchend', onPointerUp],
    ];
    handlers.forEach(([ev, fn]) => window.addEventListener(ev, fn, { passive: false }));
    return () => handlers.forEach(([ev, fn]) => window.removeEventListener(ev, fn));
  }, [onPointerMove, onPointerUp]);

  // Render the crop region to a canvas at the original resolution
  // (not the display resolution) so we keep maximum detail for OCR.
  const handleConfirm = async () => {
    if (!imgRef.current || crop.size === 0) return;
    setBusy(true);
    try {
      // We need an Image element loading the ORIGINAL data URL (not the
      // rotated one — we apply rotation manually on the canvas).
      const img = new Image();
      img.src = imgUrl;
      await new Promise((res, rej) => {
        img.onload = res;
        img.onerror = rej;
      });

      const displayedW = imgDims.w;
      const displayedH = imgDims.h;
      // Source coordinates depend on rotation. The image element is
      // displayed pre-rotated via CSS; we need to map crop-in-display
      // back to source-pixel coordinates.
      const naturalW = img.naturalWidth;
      const naturalH = img.naturalHeight;
      // For rotations 90/270 the displayed natural-axis swap; the
      // image element shows the image rotated but its bounding box
      // still reflects the rotated dimensions. We use the displayed
      // dimensions as the "post-rotation" rectangle and remap.

      const scaleX = (rotation % 180 === 0 ? naturalW : naturalH) / displayedW;
      const scaleY = (rotation % 180 === 0 ? naturalH : naturalW) / displayedH;
      // Source pixel coordinates within the *rotated* image
      const srcX = Math.round(crop.x * scaleX);
      const srcY = Math.round(crop.y * scaleY);
      const srcSize = Math.round(crop.size * Math.max(scaleX, scaleY));

      // Output canvas — square, max 1600px
      const outSize = Math.min(srcSize, 1600);
      const canvas = document.createElement('canvas');
      canvas.width = outSize;
      canvas.height = outSize;
      const ctx = canvas.getContext('2d');

      // To handle rotation: draw rotated image onto a temp canvas
      // sized to fit the rotated dimensions, then extract the cropped
      // square from it.
      const tempW = rotation % 180 === 0 ? naturalW : naturalH;
      const tempH = rotation % 180 === 0 ? naturalH : naturalW;
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = tempW;
      tempCanvas.height = tempH;
      const tctx = tempCanvas.getContext('2d');
      tctx.translate(tempW / 2, tempH / 2);
      tctx.rotate((rotation * Math.PI) / 180);
      tctx.drawImage(img, -naturalW / 2, -naturalH / 2);
      // Now extract crop from tempCanvas into outSize canvas
      ctx.drawImage(tempCanvas, srcX, srcY, srcSize, srcSize, 0, 0, outSize, outSize);

      const blob = await new Promise((res) =>
        canvas.toBlob(res, 'image/jpeg', 0.88),
      );
      onConfirm(blob);
    } catch (e) {
      console.error('Crop failed:', e);
      onCancel();
    } finally {
      setBusy(false);
    }
  };

  if (!imgUrl) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex flex-col"
      data-testid="puzzle-crop-modal"
    >
      <div className="flex items-center justify-between px-4 py-3 bg-black border-b border-white/10">
        <button
          onClick={onCancel}
          className="text-white/80 hover:text-white p-2 rounded"
          aria-label="Cancel"
          data-testid="puzzle-crop-cancel"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="text-white text-sm font-medium">
          Drag to position · drag corner to resize
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => setRotation((r) => (r + 90) % 360)}
            className="text-white/80 hover:text-white p-2 rounded"
            aria-label="Rotate"
            data-testid="puzzle-crop-rotate"
          >
            <RotateCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        className="flex-1 flex items-center justify-center overflow-hidden relative select-none touch-none"
      >
        <div className="relative inline-block">
          <img
            ref={imgRef}
            src={imgUrl}
            alt="puzzle"
            onLoad={onImgLoad}
            draggable={false}
            className="max-w-[100vw] max-h-[70vh] block select-none pointer-events-none"
            style={{
              transform: `rotate(${rotation}deg)`,
              transformOrigin: 'center',
            }}
          />
          {/* Crop overlay */}
          {imgDims.w > 0 && (
            <>
              {/* Dimming masks */}
              <div className="absolute inset-0 pointer-events-none">
                <div
                  className="absolute bg-black/60"
                  style={{ left: 0, top: 0, right: 0, height: crop.y }}
                />
                <div
                  className="absolute bg-black/60"
                  style={{ left: 0, top: crop.y, width: crop.x, height: crop.size }}
                />
                <div
                  className="absolute bg-black/60"
                  style={{
                    left: crop.x + crop.size,
                    top: crop.y,
                    right: 0,
                    height: crop.size,
                  }}
                />
                <div
                  className="absolute bg-black/60"
                  style={{
                    left: 0,
                    top: crop.y + crop.size,
                    right: 0,
                    bottom: 0,
                  }}
                />
              </div>
              {/* Crop box (draggable) */}
              <div
                onMouseDown={onPointerDown('move')}
                onTouchStart={onPointerDown('move')}
                className="absolute border-2 border-[#A46944] cursor-move"
                style={{
                  left: crop.x,
                  top: crop.y,
                  width: crop.size,
                  height: crop.size,
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0)',
                }}
                data-testid="puzzle-crop-box"
              >
                {/* Grid lines hint to help the user line up against the puzzle grid */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/30" />
                  <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/30" />
                  <div className="absolute top-1/3 left-0 right-0 h-px bg-white/30" />
                  <div className="absolute top-2/3 left-0 right-0 h-px bg-white/30" />
                </div>
                {/* Resize handle (bottom-right) */}
                <button
                  onMouseDown={onPointerDown('resize')}
                  onTouchStart={onPointerDown('resize')}
                  className="absolute -right-2.5 -bottom-2.5 w-7 h-7 bg-[#A46944] rounded-full border-2 border-white cursor-nwse-resize flex items-center justify-center"
                  aria-label="Resize crop"
                  data-testid="puzzle-crop-resize"
                >
                  <Maximize2 className="w-3.5 h-3.5 text-white" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="px-4 py-3 bg-black border-t border-white/10 flex items-center justify-between gap-3">
        <p className="text-xs text-white/60 leading-tight flex-1">
          Crop tightly around the 9×9 grid. The closer the box hugs the grid, the
          more accurate the OCR.
        </p>
        <button
          onClick={handleConfirm}
          disabled={busy || crop.size === 0}
          className="flex items-center gap-2 px-4 py-2 rounded font-medium bg-[#8B5A3C] text-white disabled:opacity-40"
          data-testid="puzzle-crop-confirm"
        >
          <Check className="w-4 h-4" />
          {busy ? 'Cropping…' : 'Use crop'}
        </button>
      </div>
    </div>
  );
}
